/**
 * Conectar uma conta de anúncios — ou trocar o token de uma que já está conectada.
 *
 * ### O token é colado aqui (ADR-0017)
 *
 * Até 05/10 esta tela recebia só o NOME de uma variável de ambiente (`env:META_ADS_TOKEN_X`)
 * e alguém com acesso ao servidor tinha de criar a variável. Seguro e impraticável: as duas
 * conexões de produção apontavam para variáveis que nunca existiram, e a tela ficava verde
 * sem nunca sincronizar. Agora o token vem por aqui, o servidor o **valida na Meta antes de
 * gravar** e o guarda cifrado — e ele nunca volta para a tela, só o fim (`…a1b2`).
 *
 * A validação desta tela é cópia da do servidor (`ads.router.ts`) e serve para a recusa ser
 * uma frase que ensina, não para defender nada: quem defende é o servidor.
 *
 * Campo `type="password"`: o token não fica à vista de quem olha a tela por cima do ombro,
 * nem no print que a pessoa manda no WhatsApp pedindo ajuda.
 */
import { useEffect, useState } from 'react';
import { Button, Dialog, Field, Input } from '@mgdlead/ui';
import { z } from 'zod';

import { FormAlert } from '@mgdlead/ui';
import { domainMessage } from '../../../lib/errors';
import { useZodForm } from '../../../lib/useZodForm';
import type { AdsConnection, AdsConnectionTest } from '../../../lib/api';
import { ResultadoDoTeste } from './ResultadoDoTeste';

/* -------------------------------------------------------------------------- */
/*  Validação — espelho de `ads.router.ts`                                      */
/* -------------------------------------------------------------------------- */

export const tokenSchema = z
  .string()
  .transform((v) => v.replace(/\s+/g, ''))
  .pipe(
    z
      .string()
      .min(1, 'Cole o token de acesso.')
      .min(30, 'Esse token está curto demais — copie o token inteiro, ele começa com EAA.')
      .max(1024, 'Isso é maior que um token. Copie só o token, sem o resto da página.')
      .regex(/^[A-Za-z0-9_\-|.]+$/, 'O token tem só letras e números. Copie de novo.'),
  );

const contaSchema = z
  .string()
  .trim()
  .min(1, 'Informe o número da conta de anúncios.')
  .max(64, 'Número de conta longo demais.')
  /** Mesma forma do servidor: numérico, com ou sem o prefixo `act_`. */
  .regex(
    /^(act_)?\d+$/,
    'A conta de anúncios é só números, com ou sem o act_ na frente. Copie do painel da Meta.',
  );

export const conectarContaSchema = z.object({
  externalAccountId: contaSchema,
  accessToken: tokenSchema,
});

const trocarTokenSchema = z.object({
  externalAccountId: z.string(),
  accessToken: tokenSchema,
});

export type ConectarContaValues = z.input<typeof conectarContaSchema>;

/* -------------------------------------------------------------------------- */
/*  O diálogo                                                                   */
/* -------------------------------------------------------------------------- */

export interface ConnectAccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** `null` = conectar conta nova; uma conexão = trocar o token DELA. */
  replacing?: AdsConnection | null;
  onSubmit: (values: { externalAccountId: string; accessToken: string }) => Promise<void>;
  /**
   * "Testar conexão" antes de salvar: pergunta à Meta e devolve o resultado, sem gravar.
   * O salvar aplica o mesmo critério no servidor — token que não passa no teste não entra.
   */
  onTest: (values: {
    externalAccountId: string;
    accessToken: string;
  }) => Promise<AdsConnectionTest>;
}

const FORM_ID = 'conectar-conta-de-anuncios';

export function ConnectAccountDialog({
  open,
  onOpenChange,
  replacing = null,
  onSubmit,
  onTest,
}: ConnectAccountDialogProps) {
  const trocando = replacing !== null;
  const [teste, setTeste] = useState<AdsConnectionTest | null>(null);
  const [testando, setTestando] = useState(false);
  const [erroDoTeste, setErroDoTeste] = useState<string | null>(null);
  const form = useZodForm({
    schema: trocando ? trocarTokenSchema : conectarContaSchema,
    initialValues: { externalAccountId: '', accessToken: '' } satisfies ConectarContaValues,
    onSubmit: async (values) => {
      try {
        await onSubmit({
          externalAccountId: values.externalAccountId,
          accessToken: values.accessToken,
        });
      } catch (erro) {
        // A recusa da Meta vem com a CAUSA escrita; a frase genérica a esconderia.
        throw domainMessage(erro) ?? erro;
      }
      onOpenChange(false);
    },
  });

  const { reset } = form;
  // Reabrir com o que sobrou da tentativa anterior conectaria a conta errada.
  useEffect(() => {
    if (open) reset({ externalAccountId: replacing?.externalAccountId ?? '', accessToken: '' });
  }, [open, replacing, reset]);

  // O resultado fala de UM token e UMA conta: mudou qualquer um, ele deixa de valer.
  useEffect(() => {
    setTeste(null);
    setErroDoTeste(null);
  }, [open, form.values.accessToken, form.values.externalAccountId]);

  const testar = async () => {
    const schema = trocando ? trocarTokenSchema : conectarContaSchema;
    const parsed = schema.safeParse(form.values);
    if (!parsed.success) {
      setErroDoTeste(
        parsed.error.issues[0]?.message ?? 'Preencha a conta e o token antes de testar.',
      );
      return;
    }
    setErroDoTeste(null);
    setTestando(true);
    try {
      setTeste(
        await onTest({
          externalAccountId: parsed.data.externalAccountId,
          accessToken: parsed.data.accessToken,
        }),
      );
    } catch (erro) {
      setErroDoTeste(domainMessage(erro) ?? 'Não conseguimos testar agora. Tente de novo.');
    } finally {
      setTestando(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={trocando ? 'Trocar o token' : 'Conectar conta de anúncios'}
      description={
        trocando
          ? `Cole o token novo para ${replacing.name ?? replacing.externalAccountId}. Conferimos com a Meta antes de salvar.`
          : 'Qual conta acompanhar e o token que lê os números dela. Conferimos com a Meta antes de salvar.'
      }
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={form.pending}>
            Cancelar
          </Button>
          {/*
            Fora do <form> — `form={FORM_ID}` é o que o liga de volta. Sem isso ele vira
            decorativo e o Enter no campo passa a ser o único jeito de enviar.
          */}
          <Button
            variant="primary"
            loading={form.pending}
            loadingLabel="Conferindo com a Meta"
            form={FORM_ID}
            type="submit"
          >
            {trocando ? 'Salvar token' : 'Conectar'}
          </Button>
        </>
      }
    >
      <form id={FORM_ID} noValidate onSubmit={form.handleSubmit} className="flex flex-col gap-3">
        {form.formError ? <FormAlert>{form.formError}</FormAlert> : null}

        {trocando ? null : (
          <Field
            label="Conta de anúncios"
            hint="O número que aparece no Gerenciador de Anúncios da Meta, no formato act_1234567890."
            error={form.errors.externalAccountId}
            required
          >
            {(control) => (
              <Input
                {...control}
                name="externalAccountId"
                autoComplete="off"
                inputMode="text"
                placeholder="act_1234567890"
                value={form.values.externalAccountId}
                invalid={form.errors.externalAccountId !== undefined}
                onChange={(event) => form.setField('externalAccountId', event.target.value)}
              />
            )}
          </Field>
        )}

        <Field
          label="Token de acesso"
          hint="O token do Usuário do Sistema, com a permissão ads_read. Ele fica guardado cifrado e não aparece mais na tela — só o final dele, para você saber qual é."
          error={form.errors.accessToken}
          required
        >
          {(control) => (
            <Input
              {...control}
              name="accessToken"
              type="password"
              autoComplete="off"
              spellCheck={false}
              placeholder="EAA…"
              value={form.values.accessToken}
              invalid={form.errors.accessToken !== undefined}
              onChange={(event) => form.setField('accessToken', event.target.value)}
            />
          )}
        </Field>

        <Button
          type="button"
          variant="secondary"
          onClick={() => void testar()}
          disabled={testando || form.pending}
          className="max-md:w-full md:self-start"
        >
          {testando ? 'Testando com a Meta…' : 'Testar conexão'}
        </Button>

        {erroDoTeste !== null ? <FormAlert tone="warning">{erroDoTeste}</FormAlert> : null}
        {teste !== null ? <ResultadoDoTeste teste={teste} /> : null}
      </form>
    </Dialog>
  );
}
